import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AppDispatch, RootState } from "../../../../redux/store";
import { fetchDGCoinProviders } from "../../../../features/auth/store/dgcoinProviderSlice";
import DGCoinProvidersTable from "./DGCoinProvidersTable";
import { CreateDGCoinProviderModal } from "../../Modal/DGCoins/CreateDGCoinProviderModal";
import {
  ExternalContainer,
  InternalContainer,
} from "../../../../components/Reusable/Layout";
import { Button } from "../../../../components/Reusable/Button";
import { EditDGCoinProviderModal } from "../../Modal/DGCoins/EditDGCoinProviderModal";
import { DeleteDGCoinProviderModal } from "../../Modal/DGCoins/DeleteDGCoinProviderModal";
import ProviderFaqManagementModal from "../../Modal/ProviderFaqManagementModal";
import { SortBy, SortOrder } from "../../../../features/auth/store/types/types";

const DGCoinProviders: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isFaqModalOpen, setIsFaqModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState<SortBy | "">("");
  const [sortOrder, setSortOrder] = useState<SortOrder | "">("");
  const [selectedProvider, setSelectedProvider] = useState<number | null>(null);
  const [selectedFaqProvider, setSelectedFaqProvider] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const { dgcoinProviders, loading, error } = useSelector(
    (state: RootState) => state.dgcoinProviders,
  );

  const isMounted = useRef(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      dispatch(fetchDGCoinProviders({}));
      return;
    }

    const timer = setTimeout(() => {
      dispatch(
        fetchDGCoinProviders({
          search: searchTerm.trim() || undefined,
          status: statusFilter || undefined,
          sort_by: sortBy || undefined,
          sort_order: sortOrder || undefined,
        }),
      );
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, sortBy, sortOrder, dispatch]);

  useEffect(() => {
    dispatch(fetchDGCoinProviders({}));
  }, [dispatch]);

  const openDeleteModal = async (id: number) => {
    setSelectedProvider(id);
    setIsDeleteModalOpen(true);
  };

  const openEditModal = async (id: number) => {
    setSelectedProvider(id);
    setIsEditModalOpen(true);
  };
  const openFaqModal = (provider: { id: number; name: string }) => {
    setSelectedFaqProvider(provider);
    setIsFaqModalOpen(true);
  };

  return (
    <ExternalContainer>
      <div className="h-full min-h-screen dark:bg-[#FFFFFF1A] w-full max-w-6xl mx-auto px-4 py-6">
        <InternalContainer headerText="DG Coin Providers">
          <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 w-full">
              <input
                type="text"
                placeholder="Search by name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border dark:border-[#FFFFFF33] dark:bg-[#1A1A1A] dark:text-white p-2 rounded text-sm w-full outline-none focus:border-blue-500"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border dark:border-[#FFFFFF33] dark:bg-[#1A1A1A] dark:text-white p-2 rounded text-sm w-full outline-none focus:border-blue-500"
              >
                <option value="">All Status</option>
                <option value="on">Active</option>
                <option value="off">Inactive</option>
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortBy)}
                className="border dark:border-[#FFFFFF33] dark:bg-[#1A1A1A] dark:text-white p-2 rounded text-sm w-full outline-none focus:border-blue-500"
              >
                <option value="">Sort By</option>
                <option value="name">Name</option>
                <option value="created_at">Date Created</option>
              </select>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as SortOrder)}
                className="border dark:border-[#FFFFFF33] dark:bg-[#1A1A1A] dark:text-white p-2 rounded text-sm w-full outline-none focus:border-blue-500"
              >
                <option value="">Sort Order</option>
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </div>
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              bgColor="bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
              textColor="text-white"
              borderRadius="rounded-lg"
              className="w-full md:w-[200px] h-10"
            >
              New Provider
            </Button>
          </div>

          <DGCoinProvidersTable
            providers={dgcoinProviders}
            loading={loading}
            searchTerm={searchTerm}
            statusFilter={statusFilter}
            onDelete={openDeleteModal}
            onEdit={openEditModal}
            onManageFaq={openFaqModal}
          />

          <CreateDGCoinProviderModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
          />

          <EditDGCoinProviderModal
            isOpen={selectedProvider !== null && isEditModalOpen}
            id={selectedProvider}
            onClose={() => {
              setIsEditModalOpen(false);
              setSelectedProvider(null);
            }}
          />

          <DeleteDGCoinProviderModal
            id={selectedProvider}
            isOpen={isDeleteModalOpen}
            onClose={() => {
              setIsDeleteModalOpen(false);
              setSelectedProvider(null);
            }}
          />
          <ProviderFaqManagementModal
            isOpen={isFaqModalOpen}
            onClose={() => {
              setIsFaqModalOpen(false);
              setSelectedFaqProvider(null);
            }}
            providerId={selectedFaqProvider?.id ?? null}
            providerName={selectedFaqProvider?.name}
            providerEndpoint="dg-coins-providers"
          />
        </InternalContainer>
        {error && <p className="text-red-500 mt-2">{error}</p>}
      </div>
    </ExternalContainer>
  );
};

export default DGCoinProviders;
